import { toggleTaskAtOffset } from '@pm/schemas';
import { ListChecks } from 'lucide-react';
import { type ComponentProps, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/cn';
import { Textarea } from './form-extras';

interface MdNode {
  type: string;
  checked?: boolean | null;
  position?: { start: { offset?: number } };
  data?: { hProperties?: Record<string, unknown> };
  children?: MdNode[];
}

interface HastNode {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

/** Guarda no <li> de cada tarefa a posição (no texto) onde o item da lista começa. */
function remarkTaskOffsets() {
  return (tree: MdNode) => {
    const walk = (node: MdNode) => {
      if (node.type === 'listItem' && typeof node.checked === 'boolean') {
        const offset = node.position?.start.offset;
        if (offset !== undefined)
          node.data = {
            ...node.data,
            hProperties: { ...node.data?.hProperties, dataTaskOffset: offset },
          };
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

/** Sanitize padrão (GitHub) + a posição da tarefa no <li>, que é só um número. */
const SANITIZE_SCHEMA = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    li: [...(defaultSchema.attributes?.li ?? []), 'dataTaskOffset'],
  },
};

/** Passa a posição do <li> para a caixa dele (direta ou dentro do primeiro parágrafo). */
function rehypeTaskOffsetToInput() {
  return (tree: HastNode) => {
    const walk = (node: HastNode) => {
      const offset = node.tagName === 'li' ? node.properties?.dataTaskOffset : undefined;
      if (offset !== undefined) {
        const direct = node.children?.find((c) => c.tagName === 'input');
        const inP = node.children
          ?.find((c) => c.tagName === 'p')
          ?.children?.find((c) => c.tagName === 'input');
        const box = direct ?? inP;
        if (box?.properties?.type === 'checkbox') box.properties.dataTaskOffset = offset;
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

const PROSE =
  'prose-pm text-base leading-relaxed text-text-1 [&_a]:text-accent [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border-strong [&_blockquote]:pl-3 [&_blockquote]:text-text-2 [&_code]:rounded-sm [&_code]:bg-surface-2 [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-[0.92em] [&_h1]:text-lg [&_h1]:font-medium [&_h2]:text-base [&_h2]:font-medium [&_h3]:font-medium [&_hr]:border-border [&_li]:my-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-surface-2 [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_table]:text-sm [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_ul]:list-disc [&_ul]:pl-5 ' +
  // Lista de tarefas: sem marcador ("-" / bolinha) antes da caixa, alinhada à esquerda.
  '[&_.contains-task-list]:list-none [&_.contains-task-list]:pl-0.5 [&_.task-list-item]:list-none [&_.task-list-item>p]:my-0.5';

/**
 * Renderização segura de Markdown (rehype-sanitize). Código em mono sobre surface-2.
 * Com `onChange`, as caixas `- [ ]` ficam clicáveis e devolvem o texto com a marcação trocada
 * (quem chama salva); sem ele, aparecem só para leitura.
 */
export function Markdown({
  children,
  className,
  onChange,
}: {
  children: string;
  className?: string;
  onChange?: (next: string) => void;
}) {
  // Estado local para a caixa responder na hora, sem esperar o salvamento voltar; texto novo
  // vindo de fora (salvou, outra pessoa editou) substitui o local.
  const [local, setLocal] = useState({ source: children, text: children });
  if (local.source !== children) setLocal({ source: children, text: children });
  const text = local.source === children ? local.text : children;

  const toggle = (offset: number) => {
    const next = toggleTaskAtOffset(text, offset);
    if (next === text) return;
    setLocal({ source: children, text: next });
    onChange?.(next);
  };

  return (
    <div className={cn(PROSE, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkTaskOffsets]}
        rehypePlugins={[[rehypeSanitize, SANITIZE_SCHEMA], rehypeTaskOffsetToInput]}
        components={{
          input: ({ node: _node, ...props }: ComponentProps<'input'> & { node?: unknown }) => {
            if (props.type !== 'checkbox') return <input {...props} />;
            const offset = Number((props as Record<string, unknown>)['data-task-offset']);
            const interactive = Boolean(onChange) && Number.isInteger(offset);
            return (
              <input
                type="checkbox"
                checked={Boolean(props.checked)}
                disabled={!interactive}
                aria-label={props.checked ? 'Desmarcar item' : 'Marcar item'}
                onChange={() => interactive && toggle(offset)}
                className={cn(
                  'mr-2 size-3.5 translate-y-[2px] accent-(--accent)',
                  interactive ? 'cursor-pointer' : 'pointer-events-none',
                )}
              />
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

/** Editor com abas Editar | Visualizar (FRONTEND_UX §8) e atalho para incluir checkbox. */
export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minRows = 5,
  autoFocus,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minRows?: number;
  autoFocus?: boolean;
  className?: string;
}) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaret = useRef<number | null>(null);

  // Depois de inserir o checkbox, devolve o cursor para logo após "- [ ] ".
  useEffect(() => {
    const caret = pendingCaret.current;
    const el = textareaRef.current;
    if (caret === null || !el) return;
    pendingCaret.current = null;
    el.focus();
    el.setSelectionRange(caret, caret);
  }, [value]);

  /** Linha nova "- [ ] " onde está o cursor (ou no fim), sem quebrar a linha atual. */
  const insertCheckbox = () => {
    if (mode !== 'edit') setMode('edit');
    const el = textareaRef.current;
    const pos = el ? el.selectionStart : value.length;
    const lineStart = value.lastIndexOf('\n', pos - 1) + 1;
    const lineEnd = value.indexOf('\n', pos);
    const end = lineEnd === -1 ? value.length : lineEnd;
    const currentLine = value.slice(lineStart, end);
    const item = '- [ ] ';
    let next: string;
    let caret: number;
    if (currentLine.trim() === '') {
      next = value.slice(0, lineStart) + item + value.slice(end);
      caret = lineStart + item.length;
    } else {
      next = `${value.slice(0, end)}\n${item}${value.slice(end)}`;
      caret = end + 1 + item.length;
    }
    pendingCaret.current = caret;
    onChange(next);
  };

  return (
    <div
      className={cn(
        'rounded border border-border-strong bg-surface-1 focus-within:border-accent focus-within:shadow-glow',
        className,
      )}
    >
      <div role="tablist" className="flex items-center gap-1 border-b border-border px-1.5 pt-1">
        {(['edit', 'preview'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={cn(
              '-mb-px border-b-2 border-transparent px-2 pb-1.5 pt-1 text-sm text-text-2 hover:text-text-1',
              mode === m && 'border-accent text-text-1',
            )}
          >
            {m === 'edit' ? 'Editar' : 'Visualizar'}
          </button>
        ))}
        <button
          type="button"
          onClick={insertCheckbox}
          title="Incluir item de checklist (- [ ])"
          className="ml-auto mb-1 inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs text-text-2 hover:bg-surface-2 hover:text-text-1"
        >
          <ListChecks className="size-3.5" /> Checkbox
        </button>
        <span className="pr-1.5 font-mono text-[10px] text-text-3">markdown</span>
      </div>
      {mode === 'edit' ? (
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={minRows}
          autoFocus={autoFocus}
          className="min-h-0 rounded-none border-0 bg-transparent focus:shadow-none"
        />
      ) : (
        <div className="min-h-[96px] px-pad-x py-2">
          {value.trim() ? (
            <Markdown onChange={onChange}>{value}</Markdown>
          ) : (
            <p className="text-sm text-text-3">Nada para visualizar.</p>
          )}
        </div>
      )}
    </div>
  );
}
